import { formatShortcut } from "@features/keymap/keyShortcutFormatter"
import { KeyOpts, KeymapContextValue, KeymapTrigger, KeymapsInfo } from "@features/keymap/types"
import { ShortcutId, shortcuts } from "@features/keymap/shortcuts"
import logger from "@utils/logger"
import React, { createContext, useCallback, useEffect, useRef } from "react"

export const KeymapContext = createContext<KeymapContextValue | null>(null)

export function KeymapProvider({ children }: { children: React.ReactNode }) {
  const bindingsRef = useRef({
    byBindingId: new Map<string, KeyOpts>(),
    byShortcut: new Map<string, string[]>(),
  })

  const handleKeyEvent = useCallback((event: KeyboardEvent, eventType: KeymapTrigger): void => {
    const shortcut = getActiveKeys(event)
    const shortcutId = `${shortcut}:${eventType}`
    const bindingIds = bindingsRef.current.byShortcut.get(shortcutId)

    if (!bindingIds || bindingIds.length <= 0) return

    const lastBindingId = bindingIds[bindingIds.length - 1]
    if (!lastBindingId) return

    const binding = bindingsRef.current.byBindingId.get(lastBindingId)
    if (!binding) return

    if (shouldIgnoreKeymap() && !binding.global) return

    if (shouldUseNativeClipboard(event, shortcut)) return

    if (event.repeat && !binding.allowRepeat && eventType === "down") return

    // only when all check's pass we can add event listener to the document
    event.preventDefault()
    binding.handler()
  }, [])

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => handleKeyEvent(event, "down")
    const keyUpHandler = (event: KeyboardEvent) => handleKeyEvent(event, "up")

    document.addEventListener("keydown", keyDownHandler)
    document.addEventListener("keyup", keyUpHandler)
    return () => {
      document.removeEventListener("keydown", keyDownHandler)
      document.removeEventListener("keyup", keyUpHandler)
    }
  }, [handleKeyEvent])

  const regKey = (id: ShortcutId, opts: KeyOpts) => {
    const key = `${id}:${opts.trigger}`
    const desc = `Press [${formatShortcut(opts.shortcut).toUpperCase()}] to ${opts.desc}`

    // Dev-mode check for duplicate ID with different shortcut
    if (process.env.NODE_ENV !== "production") {
      const existing = bindingsRef.current.byBindingId.get(key)
      if (existing && existing.shortcut !== opts.shortcut) {
        logger.warn(
          `KeymapId "${id}:${opts.trigger}" registered with different shortcuts: "${existing.shortcut}" → "${opts.shortcut}". Last registration wins.`,
        )
      }

      // Check for duplicate shortcut across different IDs
      const shortcutId = `${opts.shortcut.toLowerCase()}:${opts.trigger}`
      const existingBindings = bindingsRef.current.byShortcut.get(shortcutId)

      if (existingBindings && existingBindings.length > 0) {
        const existingIds = existingBindings.map((fullKey) => fullKey.split(":")[0]).join(", ")
        logger.warn(
          `Shortcut "${opts.shortcut}:${opts.trigger}" already used by: [${existingIds}]. New binding "${id}" will override them.`,
        )
      }
    }

    bindingsRef.current.byBindingId.set(key, { ...opts, desc })

    // Update byShortcut map
    const shortcutId = `${opts.shortcut.toLowerCase()}:${opts.trigger}`
    if (!bindingsRef.current.byShortcut.has(shortcutId)) {
      bindingsRef.current.byShortcut.set(shortcutId, [])
    }
    bindingsRef.current.byShortcut.get(shortcutId)?.push(key)
  }

  const unregKey = (id: ShortcutId, trigger: KeymapTrigger) => {
    const bindingId = `${id}:${trigger}`
    const opts = bindingsRef.current.byBindingId.get(bindingId)
    const shortcutId = `${opts?.shortcut.toLowerCase()}:${trigger}`

    const bindingIds = bindingsRef.current.byShortcut.get(shortcutId)
    if (bindingIds) {
      const filtered = bindingIds.filter((id) => id !== bindingId)
      if (filtered.length === 0) {
        bindingsRef.current.byShortcut.delete(shortcutId)
      } else {
        bindingsRef.current.byShortcut.set(shortcutId, filtered)
      }
    }
    bindingsRef.current.byBindingId.delete(bindingId)
  }

  const getKeymaps = () => {
    const keymapById = new Map<ShortcutId, KeymapsInfo>()

    for (const [key, opts] of bindingsRef.current.byBindingId.entries()) {
      const [id, trigger] = key.split(":") as [ShortcutId, KeymapTrigger]

      if (!keymapById.has(id)) {
        keymapById.set(id, {
          id: opts.id,
          shortcut: opts.shortcut,
          desc: opts.desc,
          shortDesc: shortcuts[id].desc,
          allowRepeat: opts.allowRepeat,
          global: opts.global,
          trigger: trigger,
        })
      }
    }

    return { list: [...keymapById.values()], byId: keymapById }
  }

  return (
    <KeymapContext.Provider value={{ regKey, unregKey, getKeymaps }}>
      {children}
    </KeymapContext.Provider>
  )
}

/**
 * Converts keyboard event to normalized key string (e.g. "ctrl+a", "shift", "esc").
 * @param event - Keyboard event to process
 * @returns Formatted key combination string
 */
const getActiveKeys = (event: KeyboardEvent): string => {
  const keys: string[] = []

  // Handle modifier keys pressed alone
  if (event.key === "Control") return "ctrl"
  if (event.key === "Shift") return "shift"
  if (event.key === "Alt") return "alt"
  if (event.key === "Meta") return "ctrl"

  // Keys that naturally require Shift but should not include it in the shortcut
  const shiftDependentKeys = ["+", "=", "!", "@", "#", "$", "%", "^", "&", "*", "(", ")", "_"]
  const shouldIgnoreShift = shiftDependentKeys.includes(event.key)

  // Only add modifiers if they're combined with another key
  const hasMainKey = event.key && !["Control", "Shift", "Alt", "Meta"].includes(event.key)

  if (hasMainKey) {
    if (event.ctrlKey || event.metaKey) keys.push("ctrl")
    if (event.shiftKey && !shouldIgnoreShift) keys.push("shift")
    if (event.altKey) keys.push("alt")
  }

  if (event.key === " ") keys.push("space")
  if (event.key === "Escape") keys.push("esc")
  if (["Control", "Shift", "Alt", "Meta", " ", "Escape"].includes(event.key)) {
    return keys.join("+")
  }

  if (event?.key) keys.push(event.key.toLowerCase())
  return keys.join("+")
}

/**
 * Checks if keybindings should be ignored when user is typing in text inputs.
 * @returns true if active element is input/textarea/contenteditable
 */
const shouldIgnoreKeymap = () => {
  const active = document.activeElement
  return (
    active?.tagName === "INPUT" ||
    active?.tagName === "TEXTAREA" ||
    (active instanceof HTMLElement && active.isContentEditable) ||
    active?.closest('[role="textbox"]') ||
    active?.closest('[contenteditable="true"]')
  )
}

/**
 * Lets browser-native clipboard shortcuts work when text is selected.
 * This prevents graph-level keybindings from hijacking copy/cut in non-input UI.
 */
const shouldUseNativeClipboard = (event: KeyboardEvent, shortcut: string) => {
  if (!event.ctrlKey && !event.metaKey) return false

  const isCopyOrCut = shortcut === "ctrl+c" || shortcut === "ctrl+x"
  if (!isCopyOrCut) return false

  const selection = window.getSelection()
  return !!selection && !selection.isCollapsed && selection.toString().trim().length > 0
}
